import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import useAppContext from '../../contexts/UseAppContext';
import UsePrevious from '../../helpers/UsePrevious';
import DataLoader from '../../components/DataLoader/DataLoader';
import DataError from '../../components/DataError/DataError';
import CartTitle from '../../components/CartTitle/CartTitle';
import CartItem from '../../components/CartItem/CartItem';
import PricesBox from '../../components/PricesBox/PricesBox';
import Button from '../../components/Button/Button';
import Navigation from '../../config/Navigation';
import Activity from '../../helpers/Activity';
import ExternalAnalytics from '../../helpers/ExternalAnalytics';
import Debug from '../../helpers/Debug';
import {Product as _Product} from '../../models/Product';
import {firestore, getCollectionPath} from '../../config/Firebase';
import {collection, query, where, onSnapshot} from 'firebase/firestore';
import {productConverter} from '../../models/Product';
import './Cart.css';

function Cart({section}) {
    const navigate = useNavigate();
    const context = useAppContext();
    const shoppingCart = context.shoppingCart;
    const previousCart = UsePrevious(shoppingCart);
    const allowQuantiyChange = context.cartMode === 'full';
    const scannerLinkLabel = 'Escanear otro producto';
    const paymentLinkLabel = 'Ir a pagar';
    const [isLoadingParents, setIsLoadingParents] = useState(true);
    const [parentsLoaded, setParentsLoaded] = useState(false);
    const [parentsError, setParentsError] = useState(undefined);
    const [parents, setParents] = useState([]); // This is not an error, it is needed to use the prevParents in the onSnapshot function.
    const [validProducts, setValidProducts] = useState([]);
    const [invalidProducts, setInvalidProducts] = useState([]);
    const [nonExistentItems, setNonExistentItems] = useState([]);
    const [viewMetricSent, setViewMetricSent] = useState(false);
    let unsubscribeParents = undefined;

    useEffect(() => {
        addContextMenuListener();
        if (validCart()) context.updateShoppingCartStatus('cleaning');
        return () => {
            removeContextMenuListener();
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { // This ensures that the cleaning process is done before requesting the cart items to Firestore.
        if (previousCart?.status === 'updated' && shoppingCart?.status === 'cleaning') {
            context.removeMarkedItemsFromCart();
        }
        if (previousCart?.status === 'cleaning' && shoppingCart?.status === 'updated') {
            if (validCart()) getParents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shoppingCart]);

    useEffect(() => {
        if (!validCart()) onLoadError('S&G-empty');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shoppingCart]);

    const addContextMenuListener = () => {
        document.addEventListener('contextmenu', preventContextMenu);
    };

    const removeContextMenuListener = () => {
        document.removeEventListener('contextmenu', preventContextMenu);
    };

    const preventContextMenu = (event) => {
        event.preventDefault();
    };

    const getParents = () => {
        unsubscribe();
        const productsIds = shoppingCart.listProductsAndVariantsIds();
        const queryIds = productsIds.map((item) => item.type === 'variant' ? item.parent : item.id);
        const _path = getCollectionPath('items');
        const _collection = collection(firestore, _path);
        const _where1 = where('id', 'in', queryIds);
        const _query = query(_collection, _where1).withConverter(productConverter);
        unsubscribeParents = onSnapshot(_query, (_snapshot) => {
            try {
                setParents((prevParents) => {
                    let _parents = [...prevParents];
                    _snapshot.docChanges().forEach((change) => {
                        if (change.type === 'added') {
                            _parents.push(change.doc.data());
                        } else if (change.type === 'modified') {
                            const index = _parents.findIndex(product => product.id === change.doc.id);
                            if (index >= 0) _parents[index] = change.doc.data();
                        } else if (change.type === 'removed') {
                            const index = _parents.findIndex(product => product.id === change.doc.id);
                            if (index >= 0) _parents.splice(index, 1);
                        }
                    });
                    onLoadSuccess(_parents);
                    return _parents;
                })
            } catch (error) {
                Debug.printToLog('error', error);
                onLoadError('S&G-init-error');
            }
        }, (error) => {
            Debug.printToLog('error', error);
            onLoadError('S&G-load-error');
        });
    };

    const unsubscribe = () => {
        if (unsubscribeParents) unsubscribeParents();
    };

    const getProducts = (_parents) => {
        let existentItems = [];
        let nonExistentItems = [];
        const elements = shoppingCart.listProductsAndVariantsIds();
        elements.forEach((element) => {
            const parent = element.type === 'variant' ? element.parent : element.id;
            const productMatch = _parents.find(product => product.id === parent);
            if (productMatch) {
                if (element.type === 'product') existentItems.push(productMatch.clone());
                if (element.type === 'variant') {
                    const variantMatch = productMatch.variants.find(variant => variant.id === element.id);
                    if (variantMatch) existentItems.push(variantMatch.clone());
                    else nonExistentItems.push(element);
                } else nonExistentItems.push(element);
            } else nonExistentItems.push(element);
        });
        return {existentItems, nonExistentItems};
    };

    const onLoadSuccess = (_parents) => {
        const {existentItems, nonExistentItems} = getProducts(_parents);
        const _validProducts = getValidProducts(existentItems);
        const _invalidProducts = getInvalidProducts(existentItems);
        setValidProducts(_validProducts);
        setInvalidProducts(_invalidProducts);
        setNonExistentItems(nonExistentItems);
        setParentsError(undefined);
        setParentsLoaded(true);
        setIsLoadingParents(false);
    };

    const onLoadError = (type) => {
        setParents([]);
        setValidProducts([]);
        setInvalidProducts([]);
        setParentsError(type);
        setParentsLoaded(false);
        setIsLoadingParents(false);
    };

    const getValidProducts = (_products) => {
        const _newProducts = [..._products];
        return _newProducts.filter((product) => product.isValid());
    };

    useEffect(() => {
        if (validProducts && validProducts.length > 0 && !viewMetricSent) {
            setViewMetricSent(true);
            sendMetric('view-cart');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validProducts]);

    const getInvalidProducts = (_products) => {
        const _newProducts = [..._products];
        return _newProducts.filter((product) => !product.isValid());
    };

    useEffect(() => {
        if (!isLoadingParents && parentsLoaded) updateItemsStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidProducts, nonExistentItems]);

    const updateItemsStatus = () => {
        let invalidIds = [];
        invalidProducts.map((product) => {
            const invalidItem = shoppingCart.getItemByProduct(product.id);
            if (invalidItem) invalidIds.push(invalidItem.id);
        });
        nonExistentItems.forEach((item) => {
            const nonExistentItem = shoppingCart.getItemByProduct(item.id);
            if (nonExistentItem) invalidIds.push(nonExistentItem.id);
        });
        context.updateItemsStatus(invalidIds);
    };

    const getMetadata = (additionalData, customQuantity) => {
        const metadata = {shopping_cart: shoppingCart ? shoppingCart.toActivityLog(customQuantity) : null}; // This is a snapshot of the contents of the cart when an action is performed, it's not the current state.
        if (additionalData) return {...metadata, ...additionalData};
        else return metadata;
    };

    const sendMetric = (metric) => {
        const source = validProducts && validProducts.length > 0 ? validProducts[0] : undefined;
        switch (metric) {
            case 'view-cart':
                ExternalAnalytics.viewCart(validProducts, section);
                return;
            case 'click-to-action':
                ExternalAnalytics.clickToAction(source, section, 'button', paymentLinkLabel);
                return;
            default:
                return;
        }
    };

    const hasProducts = () => {
        return (validProducts && validProducts.length > 0) || (invalidProducts && invalidProducts.length > 0);
    };

    const hasValidProducts = () => {
        return validProducts && validProducts.length > 0;
    };

    const hasInvalidProducts = () => {
        return invalidProducts && invalidProducts.length > 0;
    };

    const toHome = (error, sendExternalMetric) => {
        if (sendExternalMetric) {
            const source = validProducts && validProducts.length > 0 ? validProducts[0] : undefined;
            ExternalAnalytics.goTo(source, section, 'link', scannerLinkLabel);
        }
        Activity.log('cart-view', 'scan-button', 'click', getMetadata({error: error || null}));
        const url = Navigation.getHomeUrl();
        navigate(url);
    };

    const toProduct = (product) => {
        const productId = product.type === 'variant' ? product.parent : product.id;
        const variantId = product.type === 'variant' ? product.id : undefined;
        const storeId = context.store;
        Activity.log('cart-view', 'product-image', 'click', getMetadata({item_id: product.id || null, item_name: product.name || null, item_type: product.type || null}));
        const url = Navigation.getProductUrl(productId, storeId, variantId);
        navigate(url);
    };

    const toIdentification = () => {
        Activity.log('cart-view', 'pay-button', 'click', getMetadata());
        sendMetric('click-to-action');
        const url = Navigation.getIdentificationUrl();
        navigate(url);
    };

    const reload = (error) => {
        Activity.log('cart-view', 'reload-button', 'click', getMetadata({error: error || null}))
            .then(() => {
                Navigation.reload();
            });
    };

    const getValidTotalQuantity = () => {
        let totalQuantity = 0;
        validProducts.forEach((product) => {
            const quantity = getItemQuantity(product);
            totalQuantity += quantity;
        });
        return totalQuantity;
    };

    const getInvalidTotalQuantity = () => {
        return invalidProducts && invalidProducts.length > 0 ? invalidProducts.length : 0;
    };

    const getItemQuantity = (product) => {
        if (!product.isValid()) return 0;
        const item = shoppingCart.getItemByProduct(product.id);
        return item ? item.quantity : 0;
    };

    const getTotals = () => {
        let totals = {normalPrice: 0, offerPrice: 0, exclusivePrice: 0};
        validProducts.forEach((product) => {
            const quantity = getItemQuantity(product);
            totals.normalPrice += (product.normalPrice || 0) * quantity;
            totals.offerPrice += (product.offerPrice || 0) * quantity;
            totals.exclusivePrice += (product.exclusivePrice || 0) * quantity;
        });
        return new _Product(totals);
    };

    const onCartItemChange = (itemId, productId, quantity) => {
        const match = validProducts.find((product) => product.id === productId);
        if (match) {
            Activity.log('cart-view', 'change-quantity-button', 'click', getMetadata({item_id: match.id || null, item_name: match.name || null, item_type: match.type, new_quantity: quantity}));
            context.setCartItemQuantity(itemId, quantity);
        }
    };

    const onCartItemDelete = (itemId, productId) => {
        let newValidProducts = [...validProducts];
        const matchIndex = newValidProducts.findIndex(product => product.id === productId);
        if (matchIndex >= 0) {
            const match = newValidProducts[matchIndex].clone();
            Activity.log('cart-view', 'delete-item-button', 'click', getMetadata({item_id: match.id || null, item_name: match.name || null, item_type: match.type || null}));
            newValidProducts.splice(matchIndex, 1);
            setValidProducts(newValidProducts);
            context.removeItemsFromCart([itemId]);
        }
    };

    const clearCart = (error) => {
        Activity.log('cart-view', 'clear-cart-button', 'click', getMetadata({error: error || null}))
            .then(() => {
                context.clearShoppingCart();
            });
    };

    const validCart = () => {
        return shoppingCart && shoppingCart.hasItems();
    };

    const wrapperStyle = () => {
        return {
            marginBottom: context.cartScanButtonHeight + context.cartPayButtonHeight + context.pricesBoxMaxHeight
        };
    };

    const renderLoader = () => {
        return (
            <div className='cart_data-loader'>
                <DataLoader message='Obteniendo información de los productos...' loaderSize={undefined}/>
            </div>
        );
    };

    const renderError = () => {
        let message;
        let actionLabel;
        let action;
        switch (parentsError) {
            case 'S&G-empty':
                message = 'El carro de compras está vacío';
                actionLabel = 'Escanear un producto';
                action = () => toHome(parentsError, false);
                break;
            case 'S&G-load-error':
                message = 'Se ha producido un error al obtener la información de los productos';
                actionLabel = 'Reintentar';
                action = () => reload(parentsError);
                break;
            case 'S&G-init-error':
                message = 'Se ha producido un error al cargar los productos';
                actionLabel = 'Vaciar carro';
                action = () => clearCart(parentsError);
                break;
            default:
                break;
        }
        if (message) {
            return (
                <div className='cart_data-error'>
                    <DataError message={message} actionLabel={actionLabel} action={action} padding={20} maxWidth={undefined} iconSize={undefined} fontSize={undefined}/>
                </div>
            );
        } else return undefined;
    };

    const renderContent = () => {
        return (
            <div className='cart_wrapper' style={wrapperStyle()}>
                <div className='cart_items'>{renderItems()}</div>
                <div className='cart_footer'>
                    {hasValidProducts() && (
                        <div className='cart_totals'>
                            <PricesBox product={getTotals()} mode='cart'/>
                        </div>
                    )}
                    <div className='cart_buttons'>
                        <div className='cart_button'>
                            <Button
                                icon={undefined}
                                label={scannerLinkLabel}
                                labelColor='#FFFFFF'
                                backgroundColor='#FF3D00'
                                borderColor='#FF3D00'
                                borderWidth={0}
                                borderRadius={0}
                                padding='15px 10px'
                                paddingTarget='label'
                                margin={0}
                                width='100%'
                                minWidth={undefined}
                                maxWidth={undefined}
                                height={context.cartScanButtonHeight}
                                minHeight={undefined}
                                maxHeight={undefined}
                                transition='label'
                                fontSize={18}
                                boxShadow='unset'
                                isLoading={false}
                                loadLabel={undefined}
                                disabled={false}
                                showPulseEffect={false}
                                notificationNumber={undefined}
                                notificationBackgroundColor={undefined}
                                notificationLabelColor={undefined}
                                onClick={() => toHome(undefined, true)}
                            />
                        </div>
                        <div className='cart_button'>
                            <Button
                                icon={undefined}
                                label={paymentLinkLabel}
                                labelColor='#FFFFFF'
                                backgroundColor='#002EFF'
                                borderColor='#002EFF'
                                borderWidth={0}
                                borderRadius={0}
                                padding='15px 10px'
                                paddingTarget='label'
                                margin={0}
                                width='100%'
                                minWidth={undefined}
                                maxWidth={undefined}
                                height={context.cartPayButtonHeight}
                                minHeight={undefined}
                                maxHeight={undefined}
                                transition='label'
                                fontSize={18}
                                boxShadow='unset'
                                isLoading={false}
                                loadLabel={undefined}
                                disabled={!hasValidProducts()}
                                showPulseEffect={false}
                                notificationNumber={undefined}
                                notificationBackgroundColor={undefined}
                                notificationLabelColor={undefined}
                                onClick={toIdentification}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderItems = () => {
        const validElements = validProducts.map((product) => {
            const item = shoppingCart.getItemByProduct(product.id);
            const quantity = getItemQuantity(product);
            return (
                <CartItem
                    key={`cart-item-valid-${product.id}`}
                    itemId={item.id}
                    product={product}
                    quantity={quantity}
                    allowQuantityChange={allowQuantiyChange}
                    onImageClick={toProduct}
                    onQuantityChange={onCartItemChange}
                    onDelete={onCartItemDelete}
                />
            );
        });
        const invalidElements = invalidProducts.map((product) => {
            const item = shoppingCart.getItemByProduct(product.id);
            const quantity = getItemQuantity(product);
            return (
                <CartItem
                    key={`cart-item-invalid-${product.id}`}
                    itemId={item ? item.id : undefined}
                    product={product}
                    quantity={quantity}
                    allowQuantityChange={false}
                    onImageClick={toProduct}
                    onQuantityChange={onCartItemChange}
                    onDelete={undefined}
                />
            );
        });
        return (
            <>
                {hasValidProducts() && (
                    <>
                        <div className='cart_cart-title'>
                            <CartTitle message='Carro de compras' quantity={getValidTotalQuantity()}/>
                        </div>
                        <div className='cart_valid-items'>{validElements}</div>
                    </>
                )}
                {hasInvalidProducts() && (
                    <>
                        <div className='cart_cart-title'>
                            <CartTitle message='No disponible' quantity={getInvalidTotalQuantity()}/>
                        </div>
                        <div className='cart_invalid-items'>{invalidElements}</div>
                    </>
                )}
            </>
        );
    };

    return (
        <div className='cart'>
            {isLoadingParents && renderLoader()}
            {!isLoadingParents && !parentsLoaded && renderError()}
            {!isLoadingParents &&  parentsLoaded && !hasProducts() && renderError()}
            {!isLoadingParents &&  parentsLoaded &&  hasProducts() && renderContent()}
        </div>
    );
}

export default Cart;